import dayjs, { Dayjs } from 'dayjs';
import { CandidatesService } from '../../candidate/services/candidates.service';
import { InterviewService } from '../../job-application/services/interview.service';
import { ClarificationService } from '../../job-application/services/clarification.service';
import { DashboardClarificationRequest } from '../../job-application/models/clarification.model';
import {
  DashboardPartialError,
  DashboardPipelineStage,
  DashboardSectionKey,
  DashboardSnapshot,
} from '../models/dashboard.model';
import { FeatureJobApplications } from '../models/featurejobapplications.model';
import { FeatureJob } from '../models/featurejobs.model';
import { getDashboardWeekRange } from '../utils/dashboard.utils';
import { FeatureJobApplicationsService } from './featurejobapplications.service';
import { FeatureJobsService } from './featurejobs.service';

type PageResponse<T> = {
  content: T[];
  totalElements: number;
};

const emptyPage = <T>(): PageResponse<T> => ({
  content: [],
  totalElements: 0,
});

const settledPage = <T>(
  result: PromiseSettledResult<PageResponse<T> | undefined>
): PageResponse<T> => {
  if (result.status === 'fulfilled' && result.value) {
    return result.value;
  }
  return emptyPage<T>();
};

interface DashboardRequest {
  section: DashboardSectionKey;
  sectionLabel: string;
  promise: Promise<unknown>;
}

const ACTIVE_APPLICATION_FILTER =
  'active:eq:true';

const throwRequestError = (error: Error): never => {
  throw error;
};

const technicalMessage = (reason: unknown): string => {
  if (reason instanceof Error) {
    return reason.message;
  }

  return String(reason);
};

const requestApplicationStage = (
  status: string,
  size: number
): Promise<PageResponse<FeatureJobApplications>> =>
  FeatureJobApplicationsService.GetApplications(
    {
      page: 1,
      size,
      sort: '-appliedOn',
      filter: `${ACTIVE_APPLICATION_FILTER},status:eq:${status}`,
    },
    throwRequestError
  );

export class DashboardService {
  static async getSnapshot(
    companyId: string,
    reference: Dayjs = dayjs()
  ): Promise<DashboardSnapshot> {
    const range = getDashboardWeekRange(reference);
    const requests: DashboardRequest[] = [
      {
        section: 'openJobs',
        sectionLabel: 'Open jobs',
        promise: FeatureJobsService.Get(
          {
            page: 1,
            size: 1,
            filter: 'deleted:eq:false,closed:eq:false',
          },
          throwRequestError
        ),
      },
      {
        section: 'activeCandidates',
        sectionLabel: 'Active candidates',
        promise: CandidatesService.GetCandidates(
          companyId,
          {
            page: 1,
            size: 1,
            filter: 'deleted:eq:false,blocked:eq:false',
          },
          throwRequestError
        ),
      },
      {
        section: 'recentApplications',
        sectionLabel: 'Recent applications',
        promise: FeatureJobApplicationsService.GetApplications(
          {
            page: 1,
            size: 5,
            sort: '-appliedOn',
            filter: ACTIVE_APPLICATION_FILTER,
          },
          throwRequestError
        ),
      },
      {
        section: 'recentClarifications',
        sectionLabel: 'Candidate clarifications',
        promise: ClarificationService.recent(5),
      },
      {
        section: 'interviewAgenda',
        sectionLabel: 'Interview Agenda',
        promise: InterviewService.agenda(
          range.from,
          range.to,
          throwRequestError
        ),
      },
      {
        section: 'receivedApplications',
        sectionLabel: 'Received applications',
        promise: requestApplicationStage('RECEIVED', 1),
      },
      {
        section: 'inProgressApplications',
        sectionLabel: 'In Progress applications',
        promise: requestApplicationStage('IN_PROGRESS', 1),
      },
      {
        section: 'followUpApplications',
        sectionLabel: 'Follow Up applications',
        promise: requestApplicationStage('WAITING_FOR_FEEDBACK', 3),
      },
      {
        section: 'acceptedApplications',
        sectionLabel: 'Accepted applications',
        promise: requestApplicationStage('ACCEPTED', 1),
      },
    ];
    const results = await Promise.allSettled(
      requests.map((request) => request.promise)
    );
    const partialErrors: DashboardPartialError[] = results.flatMap(
      (result, index) => {
        if (result.status !== 'rejected') {
          return [];
        }

        const request = requests[index];
        const error: DashboardPartialError = {
          section: request.section,
          sectionLabel: request.sectionLabel,
          technicalMessage: technicalMessage(result.reason),
        };

        console.error(
          `[Dashboard] ${request.sectionLabel} request failed`,
          result.reason
        );

        return [error];
      }
    );

    const jobs = settledPage(
      results[0] as PromiseSettledResult<PageResponse<FeatureJob>>
    );
    const candidates = settledPage(
      results[1] as PromiseSettledResult<PageResponse<unknown>>
    );
    const recentApplications = settledPage(
      results[2] as PromiseSettledResult<PageResponse<FeatureJobApplications>>
    );
    const recentClarifications =
      results[3].status === 'fulfilled' && Array.isArray(results[3].value)
        ? (results[3].value as DashboardClarificationRequest[])
        : [];
    const interviews =
      results[4].status === 'fulfilled' && Array.isArray(results[4].value)
        ? results[4].value
        : [];
    const received = settledPage(
      results[5] as PromiseSettledResult<PageResponse<FeatureJobApplications>>
    );
    const inProgress = settledPage(
      results[6] as PromiseSettledResult<PageResponse<FeatureJobApplications>>
    );
    const waiting = settledPage(
      results[7] as PromiseSettledResult<PageResponse<FeatureJobApplications>>
    );
    const accepted = settledPage(
      results[8] as PromiseSettledResult<PageResponse<FeatureJobApplications>>
    );

    const pipeline: DashboardPipelineStage[] = [
      { key: 'RECEIVED', label: 'Received', count: received.totalElements },
      {
        key: 'IN_PROGRESS',
        label: 'In Progress',
        count: inProgress.totalElements,
      },
      {
        key: 'WAITING_FOR_FEEDBACK',
        label: 'Waiting',
        count: waiting.totalElements,
      },
      { key: 'ACCEPTED', label: 'Accepted', count: accepted.totalElements },
    ];

    return {
      openJobs: jobs.totalElements,
      activeCandidates: candidates.totalElements,
      followUpCount: waiting.totalElements,
      recentApplications: recentApplications.content,
      recentClarifications,
      followUpApplications: waiting.content,
      interviews,
      pipeline,
      hasPartialError: partialErrors.length > 0,
      partialErrors,
    };
  }
}
